import { formatCount } from "./count";
import { ANONYM_NEAR_DISTANCE_KM, countryOf, formatLocation } from "./location";

/** Up to this a candidate lives near the Standort: the chip turns neutral. */
const NEAR_KM = 100;

/** Words of a free-text Standort that say how, not where, the job is done. */
const WORK_MODE_RE = /^(remote|hybrid|homeoffice|home[- ]office|mobil|vor ort)\b/i;

/** The town of a job's free-text Standort („Berlin, Deutschland (Hybrid)" → „Berlin",
 * „Hybrid / Berlin" → „Berlin"), or null for a remote job or none (#163). */
export function jobTown(location: string | null | undefined): string | null {
  const parts = (location ?? "")
    .replace(/\(.*?(\)|$)/g, "") // „(Hybrid)", also an unclosed „(Hybrid"
    .split(/,|\/|\s+oder\s+/)
    .map((part) => part.trim())
    .filter(Boolean);
  // A country isn't a town: „Remote, Deutschland" names none.
  return parts.find((part) => !WORK_MODE_RE.test(part) && !countryOf(part)) ?? null;
}

export interface DistanceLabel {
  text: string;
  /** For screen readers, which read „km" as letters. */
  spoken: string;
  tone: "same" | "near" | "far";
}

/** The candidate page's distance chip: how far the candidate lives from the job's town.
 * In the Anonym-Ansicht a short distance would point at their town (#146), so it stays vague. */
export function distanceLabel(
  km: number,
  jobLocation: string | null | undefined,
  { anonym = false }: { anonym?: boolean } = {},
): DistanceLabel {
  const town = jobTown(jobLocation);
  const from = town ? ` von ${town}` : " entfernt";
  const spokenFrom = town ? ` vom Standort ${town} entfernt` : " entfernt";
  if (anonym && km < ANONYM_NEAR_DISTANCE_KM) {
    return {
      text: `unter ${ANONYM_NEAR_DISTANCE_KM} km${from}`,
      spoken: `unter ${ANONYM_NEAR_DISTANCE_KM} Kilometer${spokenFrom}`,
      tone: "same",
    };
  }
  if (km <= 0) {
    return {
      text: town ? `Gleicher Standort (${town})` : "Gleicher Standort",
      spoken: town ? `Wohnt am Standort ${town}` : "Wohnt am Standort",
      tone: "same",
    };
  }
  const distance = formatCount(km);
  return {
    text: `${distance} km${from}`,
    spoken: `${distance} Kilometer${spokenFrom}`,
    tone: km < NEAR_KM ? "near" : "far",
  };
}

export interface AnonymLocation {
  text: string;
  /** For screen readers, which read „·" and „km" badly. */
  spoken: string;
}

/** „Deutschland · 106 km von Münster": where an anonymized candidate lives,
 * without the town (#146), the distance worded like the candidate page's chip
 * (#186). Null when neither the country nor the distance is known. */
export function anonymLocation(
  country: string | null,
  km: number | null,
  jobLocation: string | null | undefined,
): AnonymLocation | null {
  const distance = km === null ? null : distanceLabel(km, jobLocation, { anonym: true });
  const text = [country, distance?.text].filter(Boolean).join(" · ");
  const spoken = [country && `Land: ${country}`, distance?.spoken].filter(Boolean).join(", ");
  return text ? { text, spoken } : null;
}

/** The location line of a candidate: the stored location, or in the Anonym-Ansicht
 * the country and distance (#146). One rule for the screens and the PDF. */
export function locationLine(
  location: string | null,
  distanceKm: number | null,
  anonym: boolean,
  jobLocation: string | null | undefined,
): AnonymLocation | null {
  if (anonym) return anonymLocation(location, distanceKm, jobLocation);
  if (!location) return null;
  const text = formatLocation(location);
  return { text, spoken: text };
}
